import { useEffect, useId, useState, type FocusEvent, type MouseEvent } from 'react'
import { NavLink, Outlet, useLocation, useMatches } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Bell, ChevronLeft, ChevronRight, HelpCircle, Logo, LogOut, Menu, Search, User, cx } from '@k1p4/ui'
import { ROLES } from '@k1p4/shared'
import { navForRoles, type NavItem } from '../nav'
import { Assistant } from '../components/Assistant'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useAuth } from '../auth/AuthProvider'
import styles from './Shell.module.css'
import { readMenuCollapsed, writeMenuCollapsed } from './menuState'
import { useDocumentTitle } from '../components/useDocumentTitle'

/** Trasy deklarują klucz nagłówka topbara przez `handle` (spec 01 §0.2 — mapa META). */
export interface RouteHandle {
  meta?: string
}

export interface ShellProps {
  /** Gałąź zasobów i18n z etykietami pozycji: `nav` (HR) albo `portalNav`. */
  navNamespace: 'nav' | 'portalNav'
  items: readonly NavItem[]
  /** Docelowy kontekst przełącznika pod nawigacją. */
  contextSwitch: { to: string; labelKey: 'shell.toPortal' | 'shell.toCompany' }
  /** Klucz w gałęzi `meta`, gdy trasa nie poda własnego. */
  fallbackMetaKey: string
  /** Kontekst asystenta — decyduje, o które reguły pytamy serwer. */
  assistantScope: 'app' | 'portal'
  /**
   * Wariant powłoki (fala 8, grupa 163): portal ma pod logo podpis „PORTAL
   * PRACOWNIKA", węższą wyszukiwarkę dokumentów i bez skrótu ⌘K (spec 03 §0).
   */
  variant?: 'app' | 'portal'
}

/**
 * Inicjały do awatara. Sesja nie niesie imienia ani nazwiska (`users` ma tylko
 * adres), więc bierzemy je z części lokalnej adresu: „anna.kowalska" → „AK".
 * Nie wymyślamy tu osoby — makieta pokazuje „MN / Marta Nowak", ale to persona
 * projektanta, a instancja startuje pusta [D19].
 */
function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? ''
  const parts = local.split(/[._-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  return local.slice(0, 2).toUpperCase()
}

/** Podpowiedź pozycji zwiniętego menu — liczona z prostokąta pozycji, więc `overflow` menu jej nie ucina. */
interface NavTip {
  label: string
  top: number
  left: number
}

function tipFor(label: string, el: HTMLElement): NavTip {
  const rect = el.getBoundingClientRect()
  return { label, top: rect.top + rect.height / 2, left: rect.right }
}

function useMetaKey(fallback: string): string {
  const matches = useMatches()
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle as RouteHandle | undefined
    if (handle?.meta !== undefined) return handle.meta
  }
  return fallback
}

export function Shell({
  navNamespace,
  items,
  contextSwitch,
  fallbackMetaKey,
  assistantScope,
  variant = 'app',
}: ShellProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [seenPath, setSeenPath] = useState(location.pathname)
  const sidebarId = useId()
  const [collapsed, setCollapsed] = useState(readMenuCollapsed)
  const [tip, setTip] = useState<NavTip | null>(null)
  const metaKey = useMetaKey(fallbackMetaKey)
  /* Tytuł karty z nagłówka widoku (fala 9, grupa 143; WCAG 2.4.2). */
  useDocumentTitle(t(`meta.${metaKey}.title`))
  const { session, logout } = useAuth()

  /* Nawigacja pokazuje wyłącznie moduły dostępne dla ról użytkownika (§5.3). */
  const roles = session?.user.roles ?? []
  const visibleItems = navForRoles(items, roles)

  /*
   * Etykieta roli: pierwsza z posiadanych w kolejności ROLES (hr_admin →
   * payroll → manager → employee), czyli ta o najszerszym zakresie.
   */
  const primaryRole = ROLES.find((role) => roles.includes(role))

  /*
   * Przełącznik kontekstu pokazujemy tylko wtedy, gdy druga aplikacja jest
   * realnie dostępna: portal wymaga roli employee, aplikacja HR — dowolnej
   * innej. Link do miejsca, które odpowie 403, byłby atrapą [D19].
   */
  const showContextSwitch =
    contextSwitch.to === '/me'
      ? roles.includes('employee')
      : roles.some((role) => role !== 'employee')

  /*
   * Zmiana trasy zamyka szufladę mobilną — inaczej zasłaniałaby nowy widok.
   * Korekta stanu w trakcie renderu (a nie w efekcie) unika kaskady renderów
   * i obsługuje też nawigację przyciskiem „wstecz".
   */
  if (seenPath !== location.pathname) {
    setSeenPath(location.pathname)
    setMenuOpen(false)
  }

  /* Escape chowa podpowiedź także wtedy, gdy fokus zostaje na pozycji (WCAG 1.4.13). */
  useEffect(() => {
    if (!tip) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTip(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [tip])

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    setTip(null)
    writeMenuCollapsed(next)
  }

  /* Podpowiedź ma sens tylko bez widocznej etykiety, czyli w trybie ikon. */
  const showTip = (label: string) => (event: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
    if (collapsed) setTip(tipFor(label, event.currentTarget))
  }
  const hideTip = () => setTip(null)

  return (
    <div className={cx(styles.root, collapsed && styles.rootCollapsed)}>
      {/* Pierwszy cel klawiatury: przeskok nad nawigacją do treści (fala 9, grupa 153; WCAG 2.4.1). */}
      <a href="#tresc" className={styles.skipLink}>
        {t('shell.skipToContent')}
      </a>
      <aside
        id={sidebarId}
        className={cx(styles.sidebar, menuOpen && styles.sidebarOpen, collapsed && styles.sidebarCollapsed)}
        aria-label={t('shell.mainNav')}
      >
        <div className={styles.brand}>
          {/* Oba warianty są w DOM; który widać, rozstrzyga media query — na telefonie przełącznika nie ma, więc zapamiętane zwinięcie nie może zmieniać marki. */}
          <span className={styles.brandFull}>
            {variant === 'portal' ? (
              <Logo size={38} withWordmark withTagline tagline={t('shell.portalTagline')} />
            ) : (
              <Logo size={38} withWordmark withTagline />
            )}
          </span>
          {collapsed ? (
            <span className={styles.brandMark} data-testid="brand-mark">
              <Logo size={38} />
            </span>
          ) : null}
        </div>

        <button
          type="button"
          className={styles.collapseToggle}
          aria-label={collapsed ? t('shell.expandMenu') : t('shell.collapseMenu')}
          aria-expanded={!collapsed}
          aria-controls={sidebarId}
          onClick={toggleCollapsed}
          onMouseEnter={showTip(collapsed ? t('shell.expandMenu') : t('shell.collapseMenu'))}
          onMouseLeave={hideTip}
          onFocus={showTip(collapsed ? t('shell.expandMenu') : t('shell.collapseMenu'))}
          onBlur={hideTip}
        >
          <span className={cx(styles.collapseIcon, collapsed && styles.collapseIconFlipped)} aria-hidden="true">
            <ChevronLeft size={18} />
          </span>
          <span className={styles.collapseText}>
            {collapsed ? t('shell.expandMenu') : t('shell.collapseMenu')}
          </span>
        </button>

        <nav className={styles.nav}>
          <ul className={styles.navList}>
            {visibleItems.map((item) => {
              const Icon = item.icon
              const label = t(`${navNamespace}.${item.key}`)
              return (
                <li key={item.key}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/me'}
                    aria-label={label}
                    onMouseEnter={showTip(label)}
                    onMouseLeave={hideTip}
                    onFocus={showTip(label)}
                    onBlur={hideTip}
                    className={({ isActive }) =>
                      cx(styles.navLink, isActive && styles.navLinkActive)
                    }
                  >
                    <span className={styles.navIcon}>
                      <Icon size={18} />
                    </span>
                    <span className={styles.navLabel}>{label}</span>
                  </NavLink>
                </li>
              )
            })}
            {/*
             * Podręcznik (ADR-0062) stoi poza APP_NAV/PORTAL_NAV: nie jest modułem
             * i nie ma uprawnienia, więc `navForRoles` go nie filtruje, a
             * `firstAllowedAppPath` nie może na niego wskazać. Ten sam markup co
             * pozycje modułów — zwinięte menu, podpowiedź i szuflada mobilna działają bez nowego CSS.
             */}
            <li>
              <NavLink
                to="/docs"
                aria-label={t('shell.help')}
                onMouseEnter={showTip(t('shell.help'))}
                onMouseLeave={hideTip}
                onFocus={showTip(t('shell.help'))}
                onBlur={hideTip}
                className={({ isActive }) => cx(styles.navLink, isActive && styles.navLinkActive)}
              >
                <span className={styles.navIcon}>
                  <HelpCircle size={18} />
                </span>
                <span className={styles.navLabel}>{t('shell.help')}</span>
              </NavLink>
            </li>
          </ul>
        </nav>

        {showContextSwitch ? (
          <div className={styles.context}>
            <p className={styles.contextLabel}>{t('shell.contextLabel')}</p>
            <NavLink
              to={contextSwitch.to}
              className={styles.contextLink}
              aria-label={t(contextSwitch.labelKey)}
              onMouseEnter={showTip(t(contextSwitch.labelKey))}
              onMouseLeave={hideTip}
              onFocus={showTip(t(contextSwitch.labelKey))}
              onBlur={hideTip}
            >
              <span className={styles.contextLinkText}>
                <User size={18} />
                <span className={styles.contextLinkLabel}>{t(contextSwitch.labelKey)}</span>
              </span>
              <span className={styles.contextChevron}>
                <ChevronRight size={16} />
              </span>
            </NavLink>
          </div>
        ) : null}

        <div className={styles.sidebarFooter}>
          <p className={styles.ecosystem}>
            {t('shell.ecosystemLabel')}
            <br />
            <span className={styles.ecosystemMark}>{t('shell.ecosystemMark')}</span>
          </p>
          {session ? (
            <div className={styles.user}>
              <span className={styles.avatar} aria-hidden="true">
                {initialsFromEmail(session.user.email)}
              </span>
              <span className={styles.userLines}>
                <span className={styles.userName} title={session.user.email}>
                  {session.user.email}
                </span>
                {primaryRole ? (
                  <span className={styles.userRole}>{t(`roleNames.${primaryRole}`)}</span>
                ) : null}
              </span>
              <button
                type="button"
                className={styles.logout}
                onClick={() => void logout()}
                aria-label={t('shell.logout')}
                onMouseEnter={showTip(t('shell.logout'))}
                onMouseLeave={hideTip}
                onFocus={showTip(t('shell.logout'))}
                onBlur={hideTip}
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : null}
        </div>
      </aside>

      {collapsed && tip ? (
        <div className={styles.navTip} style={{ top: tip.top, left: tip.left }} aria-hidden="true">
          {tip.label}
        </div>
      ) : null}

      <button
        type="button"
        className={cx(styles.scrim, menuOpen && styles.scrimOpen)}
        aria-label={t('shell.closeMenu')}
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />

      <div className={styles.content}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.burger}
            aria-label={menuOpen ? t('shell.closeMenu') : t('shell.openMenu')}
            aria-expanded={menuOpen}
            aria-controls={sidebarId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Menu />
          </button>

          <div className={styles.heading}>
            <p className={styles.kicker}>{t(`meta.${metaKey}.kicker`)}</p>
            <h1 className={styles.title} title={t(`meta.${metaKey}.title`)}>
              {t(`meta.${metaKey}.title`)}
            </h1>
          </div>

          <div className={styles.topbarActions}>
            {/*
              Atrapy z makiety pozostają jawnie wyłączone do czasu realnej
              funkcji [D19]. To wyłączone PRZYCISKI, nie `span`-y z aria-label:
              atrybut nazwy dostępnej wolno nieść tylko elementom z rolą, więc
              span udawałby kontrolkę także przed czytnikiem ekranu.
            */}
            <button
              type="button"
              className={cx(styles.search, variant === 'portal' && styles.searchPortal)}
              disabled
              title={t('shell.searchSoon')}
            >
              <Search size={16} />
              <span className={styles.searchLabel}>
                {t(variant === 'portal' ? 'shell.portalSearchPlaceholder' : 'shell.searchPlaceholder')}
              </span>
              {variant === 'portal' ? null : <span className={styles.searchKbd}>⌘K</span>}
            </button>
            <button
              type="button"
              className={styles.iconButton}
              disabled
              title={t('shell.notificationsSoon')}
              aria-label={t('shell.notifications')}
            >
              <Bell size={18} />
            </button>
            <LanguageSwitcher />
          </div>
        </header>

        <main id="tresc" tabIndex={-1} className={styles.main}>
          <Outlet />
        </main>

        {/* Asystent stoi nad treścią w obu aplikacjach — pyta serwer o widok. */}
        <Assistant scope={assistantScope} metaKey={metaKey} />
      </div>
    </div>
  )
}
